import React, { useEffect } from "react";
import { Button, Box, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { sendCommand, setLastUsedCommandFromOverview, clearPendingCommandFromOverview } from "../../../ducks/uiDuck";
import { useOverviewUI, useSystemId } from "../../../hooks/useReduxSelectors";
import { getDevicesListFromDevicesDuck } from "../../../ducks/graphicsDuck";

// Fallback timeout for the pending/spinner state on a command button. If the device never
// confirms the command (e.g. it's offline/unreachable), the spinner is cleared after this
// delay instead of being stuck indefinitely.
const COMMAND_PENDING_TIMEOUT_MS = 10000;


const CommandsList = React.memo(({ hwId, commands, diagnostics }) => {

  const dispatch = useDispatch();
  const systemId = useSystemId();
  const overview = useOverviewUI();
  const commandsStatusList = overview?.commandsStatusList;
  const lastUserCommandFromOverview = overview?.lastUserCommandFromOverview;
  useEffect(() => {
    // When certain devices diagnostics values change, fire getDevicesListFromDevicesDuck redux-thunk-function which we use to update the commandsStatusList in uiDuck.js.
    if (hwId === "DIGI_SCU" || hwId === "SO2_SCRUBBER") {
      dispatch(getDevicesListFromDevicesDuck({ systemId }));
    }
  }, [diagnostics]);

  const onHandleCommand = (command) => {
    const isCommandOpen = commandsStatusList.some(commandStatus => {
      const itemNameWithoutWhiteSpaces = command.name.replace(/\s+/g, '').toLowerCase();
      if (commandStatus.hwId === hwId && commandStatus.commandName.replace(/\s+/g, '').toLowerCase() === itemNameWithoutWhiteSpaces) {
        return commandStatus.isCommandOpen;
      } else {
        return false;
      }
    });
    const group = command.group ? command.group : undefined;
    const isToggle = command.toggle;
    const isActivating = isCommandOpen ? false : true;
    let commandToSend;
    if (isToggle) {
      commandToSend = isCommandOpen ? command.release : command.press;
    } else if (!isToggle) {
      commandToSend = command.press;
    }
    // Dispatch setLastUsedCommand for Overview
    if (hwId === "SO2_SCRUBBER") {
      dispatch(setLastUsedCommandFromOverview({
        hwId: hwId,
        command: command.name,
        isActivating: isActivating,
        isPending: isCommandOpen ? false : true,
        systemId
      }));
    } else {
      dispatch(setLastUsedCommandFromOverview({
        hwId: hwId,
        command: command.name,
        isActivating: isActivating,
        systemId
      }));
    }
    dispatch(
      sendCommand({
        argsProps: {
          HwId: hwId,
          Command: commandToSend,
          Value: command.name,
        },
        commandType: {
          isToggle,
          isActivating: isActivating,
        },
        systemId,
        group,
      })
    );

    // Safety-net: if the device never confirms the command (offline/unreachable), make sure
    // the button's pending spinner doesn't stay stuck forever.
    setTimeout(() => {
      dispatch(clearPendingCommandFromOverview({ hwId, command: command.name }));
    }, COMMAND_PENDING_TIMEOUT_MS);
  };


  function checkIsCommandOpen(item, hwId, commandsStatusList) {
    const itemNameWithoutWhiteSpaces = item.name.replace(/\s+/g, '').toLowerCase();

    // Return a boolean using some() and checking if hwId and commandName matches and commandIsOpen
    return commandsStatusList.some(commandStatus =>
      commandStatus.hwId === hwId &&
      commandStatus.commandName.replace(/\s+/g, '').toLowerCase() === itemNameWithoutWhiteSpaces &&
      commandStatus.isCommandOpen
    );
  }

  function checkIsCommandPending(item, lastUserCommandFromOverview, commandsStatusList) {
    const itemNameWithoutWhiteSpaces = item.name.replace(/\s+/g, '');

    if (lastUserCommandFromOverview.hwId === "SO2_SCRUBBER") {
      if (lastUserCommandFromOverview.hwId === hwId && lastUserCommandFromOverview.command === itemNameWithoutWhiteSpaces) {
        return lastUserCommandFromOverview.isPending;
      }
      return false;
    } else { // For other hwIds (meaning SCU)
      return commandsStatusList.some(commandStatus => {
        const lastCommandNameWithoutWhiteSpaces = lastUserCommandFromOverview.command.replace(/\s+/g, '');
        const isHwIdMatch = commandStatus.hwId === lastUserCommandFromOverview.hwId;
        const isCommandNameMatch = commandStatus.commandName === itemNameWithoutWhiteSpaces;
        const isLastCommandNameMatch = commandStatus.commandName === lastCommandNameWithoutWhiteSpaces;
        const isStatusMismatch = commandStatus.isCommandOpen !== lastUserCommandFromOverview.isActivating;

        // Check all conditions to determine if the command is pending
        const pendingBoolean = isHwIdMatch && isCommandNameMatch && isLastCommandNameMatch && isStatusMismatch;
        return pendingBoolean;
      });
    }
  }



  return (
    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
      {commands &&
        commands.map((item, idx) => {

          const isCommandOpen = checkIsCommandOpen(item, hwId, commandsStatusList);
          const isCommandPending = checkIsCommandPending(item, lastUserCommandFromOverview, commandsStatusList);

          return (
            <Button
              key={`${item.name}_${idx}`}
              type="button"
              color={isCommandOpen ? "secondary" : "dark"}
              disabled={isCommandPending}
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                m: "2px",
                ...(item.name.length > 22 && { minWidth: 180 }),
                ...(isCommandPending && { animation: "pulse 1.5s infinite", opacity: 0.7 }),
              }}
              onClick={() => onHandleCommand(item)}
            >
              <i className={`icon-custom-${item.icon}`} />
              <div>
                {isCommandPending && <CircularProgress size={16} />}
                {item.name}
              </div>
            </Button>
          );
        })}
    </Box>
  );
});



export default CommandsList;